// CONSTRAINT-1 D9 — « Aujourd'hui un score plafonné ne dit pas pourquoi. »
//
// Deux rendus, un seul calcul :
//   • `compact`  — l'étiquette de la ligne repliée. C'est elle qui porte le verdict, et
//     c'est la seule chose qu'un BM lit dans le flux : elle doit se suffire.
//   • complet    — le détail, toujours déployé. Il tient en quelques lignes, et le
//     replier obligeait à cliquer pour une information qui a sa place à l'écran
//     (arbitrage Axel du 2026-08-16). Le motif n'est pas répété ici : l'étiquette le dit.
//
// Le bloc couvre la chaîne ENTIÈRE, pas seulement les contraintes : le score naît d'un
// mélange pondéré critères/IA, que l'étage déterministe corrige ensuite. Ne montrer que
// les 5 dimensions sous le titre « comment le score s'est construit » laissait croire
// qu'elles faisaient tout le travail.
//
// Toute la règle de lecture vit dans `explainScore()` / `dimensionRows()` / `scoreChain()`
// (lib/mappers/match.mapper.ts) — ce composant ne fait que les rendre.
//
// Partagé entre la vue mission (MissionMatchResults) et la vue moteur
// (MatchingEngineView) : la duplication de ce genre de logique d'affichage est
// exactement ce qui avait fait diverger le mapping des matchs avant P0.6.

import React from 'react';
import { Ban, CheckCircle2, Gauge, MinusCircle, TrendingDown } from 'lucide-react';
import {
    explainScore, dimensionRows, scoreChain, eligibilityLabelText,
    type ScoreBreakdown, type EligibilityLabel, type DimensionRow,
} from '../../lib/mappers/match.mapper';

interface Props {
    breakdown?: ScoreBreakdown | null;
    /** Score affiché au candidat — sert à montrer la soustraction en entier. */
    score?: number;
    /** Étiquette d'une ligne, pour la rangée repliée. */
    compact?: boolean;
}

const LABEL_STYLES: Record<EligibilityLabel, string> = {
    valide: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    avec_reserve: 'text-amber-700 bg-amber-50 border-amber-200',
    rejete: 'text-red-700 bg-red-50 border-red-200',
};

/**
 * La couleur suit la GRADATION du moteur, pas un simple binaire : vert quand ça
 * correspond, orange pour un écart léger ou modéré — négociable — et rouge à partir de
 * « important », où la doctrine cesse de considérer que ça se discute.
 *
 * Le gris est réservé au non évaluable. Il pourrait être orange, mais l'orange dit
 * désormais « écart négociable » : deux sens sur une même couleur rendrait la colonne
 * illisible. L'absence de donnée n'est pas un écart, c'est un trou.
 */
function rowStyle(r: DimensionRow): { row: string; icon: React.ReactNode; impact: string } {
    if (r.state === 'unknown') {
        return {
            row: 'bg-slate-50',
            icon: <MinusCircle size={13} className="text-slate-300" />,
            impact: 'text-slate-400',
        };
    }
    if (r.level === 'minor' || r.level === 'moderate') {
        return {
            row: 'bg-amber-50/70',
            icon: <TrendingDown size={13} className="text-amber-500" />,
            impact: 'text-amber-700',
        };
    }
    if (r.state === 'cost') {
        return {
            row: 'bg-red-50/70',
            icon: <Ban size={13} className="text-red-500" />,
            impact: 'text-red-700',
        };
    }
    return {
        row: 'bg-emerald-50/60',
        icon: <CheckCircle2 size={13} className="text-emerald-500" />,
        impact: 'text-emerald-700',
    };
}

const ScoreExplanation: React.FC<Props> = ({ breakdown, score, compact = false }) => {
    const x = explainScore(breakdown);
    const rows = dimensionRows(breakdown);
    const chain = scoreChain(breakdown);

    // ── Étiquette de ligne ───────────────────────────────────────────────────
    if (compact) {
        if (!x.hasContent) return null;
        const badge =
            x.driver === 'knockout' ? 'text-red-700 bg-red-50 border-red-200'
            : x.driver === 'cap' || x.driver === 'domain' ? 'text-orange-700 bg-orange-50 border-orange-200'
            : 'text-amber-800 bg-amber-50 border-amber-200';
        const Icon =
            x.driver === 'knockout' ? Ban
            : x.driver === 'cap' || x.driver === 'domain' ? Gauge
            : TrendingDown;
        const text =
            x.driver === 'knockout' ? 'Écarté — condition rédhibitoire'
            : x.cap !== null ? `Plafonné à ${x.cap.value} — ${x.cap.label}`
            : x.headline;
        return (
            <span
                className={`flex items-center gap-1 text-[10px] font-bold border rounded px-1.5 py-0.5 ${badge}`}
                title={x.headline}
            >
                <Icon size={9} /> {text}
            </span>
        );
    }

    if (rows.length === 0 && !chain) return null;

    return (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between gap-2">
                <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    Comment le score s'est construit
                </h4>
                {x.eligibilityLabel && (
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border whitespace-nowrap ${LABEL_STYLES[x.eligibilityLabel]}`}>
                        {eligibilityLabelText(x.eligibilityLabel)}
                    </span>
                )}
            </div>

            {/* L'amont : le mélange pondéré. Les 5 dimensions n'agissent qu'après. */}
            {chain && (
                <div className="px-3 py-2 border-b border-slate-100 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[11px]">
                    <span className="text-slate-500">Critères</span>
                    <span className="font-bold text-slate-800 tabular-nums">{chain.criteria.score}</span>
                    <span className="text-slate-400">×{Math.round(chain.criteria.weight * 100)}%</span>
                    <span className="text-slate-300">+</span>
                    <span className="text-slate-500">IA</span>
                    <span className="font-bold text-slate-800 tabular-nums">{chain.ia.score}</span>
                    <span className="text-slate-400">×{Math.round(chain.ia.weight * 100)}%</span>
                    <span className="text-slate-300">=</span>
                    <span className={`font-bold tabular-nums ${chain.adjusted ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                        {chain.blended}
                    </span>
                    {chain.adjusted && (
                        <>
                            <span className="text-slate-300">→</span>
                            <span className="font-black text-slate-900 tabular-nums">{chain.final}</span>
                            <span className="text-slate-400">après contraintes</span>
                        </>
                    )}
                </div>
            )}

            {x.driver === 'knockout' && breakdown?.knockout_reason && (
                <p className="px-3 py-2 text-[11px] text-red-700 bg-red-50 leading-snug border-b border-red-100">
                    {breakdown.knockout_reason}
                </p>
            )}

            {rows.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="text-[9px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50/70">
                                <th className="px-2 py-1.5 w-5" />
                                <th className="px-1 py-1.5">Dimension</th>
                                {/* La mission d'abord : c'est l'attendu, le candidat s'y compare. */}
                                <th className="px-2 py-1.5">Mission</th>
                                <th className="px-2 py-1.5">Candidat</th>
                                <th className="px-2 py-1.5 text-right whitespace-nowrap">Impact</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white">
                            {rows.map((r) => {
                                const s = rowStyle(r);
                                return (
                                    <tr key={r.dimension} className={s.row}>
                                        <td className="px-2 py-2 align-top">{s.icon}</td>
                                        <td className="px-1 py-2 align-top text-xs font-bold text-slate-700 whitespace-nowrap">
                                            {r.label}
                                        </td>
                                        {r.state === 'unknown' ? (
                                            // Rien à comparer : une seule cellule centrée sur les
                                            // deux colonnes, plutôt qu'un vide de chaque côté.
                                            <td colSpan={2} className="px-2 py-2 align-top text-center text-[11px] italic text-slate-400">
                                                non évaluable — {!r.talent ? 'donnée absente du profil' : 'la mission ne la précise pas'}
                                            </td>
                                        ) : (
                                            <>
                                                <td className="px-2 py-2 align-top text-[11px] leading-snug text-slate-600">
                                                    {r.mission}
                                                </td>
                                                <td className="px-2 py-2 align-top text-[11px] leading-snug">
                                                    <span className="text-slate-800 font-medium">{r.talent}</span>
                                                    {r.state === 'cost' && (
                                                        <span className="block text-slate-500">{r.levelLabel}</span>
                                                    )}
                                                </td>
                                            </>
                                        )}
                                        <td className="px-2 py-2 align-top text-right whitespace-nowrap">
                                            {r.impact && (
                                                <span className={`text-[10px] font-bold tabular-nums ${
                                                    r.impact === 'non appliqué'
                                                        ? 'text-slate-400 font-medium italic'
                                                        : s.impact
                                                }`}>
                                                    {r.impact}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* L'arithmétique du retrait, seulement quand elle tombe juste (cf. explainScore). */}
            {x.scoreBeforePenalty !== null && score !== undefined && (
                <div className="px-3 py-2 bg-slate-50 border-t border-slate-100 flex items-baseline justify-between gap-3 text-xs">
                    <span className="text-slate-500">
                        {x.scoreBeforePenalty} avant retraits, −{x.penaltyApplied} au total
                    </span>
                    <span className="font-black text-slate-800 tabular-nums">{score}</span>
                </div>
            )}
        </div>
    );
};

export default ScoreExplanation;
